import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { NumberRow } from "@/components/NumberPill";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getSavedPicks, deletePick, getCheckedResults } from "@/lib/local-storage";
import { formatDate } from "@/lib/format";
import { Star, Trash2, Sparkles, CheckCircle, Cloud, CloudOff } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { listSavedPicks, deleteSavedPick } from "@/lib/saved-picks.functions";
import { migrateLocalPicksToCloud } from "@/lib/sync-picks";
import { toast } from "sonner";

export const Route = createFileRoute("/my-picks")({
  head: () => buildSeo({
    title: "My Picks — Saved Numbers & Results",
    description: "View your saved lottery number picks and checked results.",
    path: "/my-picks",
    noindex: true,
  }),
  component: MyPicks,
});

function MyPicks() {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const listFn = useServerFn(listSavedPicks);
  const deleteFn = useServerFn(deleteSavedPick);
  const [localPicks, setLocalPicks] = useState(typeof window !== "undefined" ? getSavedPicks() : []);
  const [checked] = useState(typeof window !== "undefined" ? getCheckedResults() : []);

  // Migrate localStorage picks once signed in
  useEffect(() => {
    if (user) {
      migrateLocalPicksToCloud().then(() => {
        queryClient.invalidateQueries({ queryKey: ["saved_picks"] });
      });
    }
  }, [user, queryClient]);

  const { data: cloudPicks = [] } = useQuery({
    queryKey: ["saved_picks"],
    queryFn: () => listFn(),
    enabled: !!user,
  });

  const deleteCloud = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved_picks"] });
      toast.success("Deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleLocalDelete(id: string) {
    deletePick(id);
    setLocalPicks(getSavedPicks());
  }

  const picks = user
    ? cloudPicks.map((p) => ({
        id: p.id,
        gameName: p.game_name,
        gameSlug: p.game_slug,
        numbers: p.numbers,
        bonusNumbers: p.bonus_numbers,
        createdAt: p.created_at,
      }))
    : localPicks;

  return (
    <Layout>
      <SEOHead title="My Picks — Saved Numbers & Results" description="Your saved picks and checked results." />
      <div className="container py-8 max-w-2xl">
        <h1 className="font-sans flex items-center gap-2 text-[30px] font-bold leading-tight mb-6 tracking-tight">
          <Star className="h-7 w-7 text-accent" strokeWidth={2.25} /> My Lottery Picks and Results
        </h1>
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-sans flex items-center gap-2 text-[20px] font-bold leading-7 tracking-tight">
              <Sparkles className="h-5 w-5 text-primary" strokeWidth={2.25} /> Saved Numbers ({picks.length})
            </h2>
            {authLoading ? null : user ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Cloud className="h-3.5 w-3.5" /> Synced across your devices
              </span>
            ) : null}
          </div>
          {picks.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No saved picks yet. <Link to="/tools/number-generator" className="text-primary hover:underline">Generate some numbers!</Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {picks.map((p) => (
                <Card key={p.id}>
                  <CardContent className="py-3 px-4 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{p.gameName}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(p.createdAt)}</span>
                      </div>
                      <NumberRow numbers={p.numbers} bonusNumbers={p.bonusNumbers} size="sm" />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete pick"
                      onClick={() => (user ? deleteCloud.mutate(p.id) : handleLocalDelete(p.id))}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-sans flex items-center gap-2 text-[20px] font-bold leading-7 mb-4 tracking-tight">
            <CheckCircle className="h-5 w-5 text-primary" strokeWidth={2.25} /> Checked Results ({checked.length})
          </h2>
          {checked.length === 0 && (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              No checked results yet. <Link to="/tools/results-checker" className="text-primary hover:underline">Check your numbers!</Link>
            </CardContent></Card>
          )}
        </section>
      </div>
    </Layout>
  );
}